import { VStack, Text, HStack, View } from "@gluestack-ui/themed";
import { Pressable } from "react-native";
import { MotiView } from "moti";

import { Icon } from "@repo/core/components/core/Icon";
import Spinner from "@repo/core/components/core/Spinner";

import { CardCollapseProps } from "./types";
import { useCardCollapseController } from "./controller";

export const CardCollapse = ({
  title = "",
  subtitle = "",
  children,
  variant,
  collapseContentFixedHeight,
  isLoading
}: CardCollapseProps) => {
  const {
    borderColor,
    icon,
    iconColor,
    contentRef,
    isCollapsed,
    toggleCollapse,
    handleCollapseContentLayout,
    animationState
  } = useCardCollapseController({ variant, isLoading });

  return (
    <VStack
      borderWidth={1}
      borderRadius={"$lg"}
      backgroundColor="$backgroundLight0"
      borderColor={borderColor}
    >
      {/* Leading */}
      <HStack p="$2" gap="$3" justifyContent="flex-start" alignItems="center">
        <VStack w="$6" h="$6" alignItems="center" justifyContent="center">
          {isLoading ? (
            <Spinner size="small" style={{ transform: "scale(1.15)" }} />
          ) : (
            <Icon name={icon} size="sm" color={iconColor} />
          )}
        </VStack>

        <VStack flex={1}>
          <Text size="sm" fontWeight={600} color="$textLight900">
            {title}
          </Text>
          {subtitle && (
            <Text size="xs" color="$textLight900">
              {subtitle}
            </Text>
          )}
        </VStack>

        {/* Trigger - Only show if there's content */}
        {children && (
          <Pressable onPress={toggleCollapse}>
            <View p="$1" px="$2">
              <Icon name={isCollapsed ? "caret-up" : "caret-down"} size="xs" />
            </View>
          </Pressable>
        )}
      </HStack>

      {/* Collapsed content - Always render children but animate visibility */}
      {children && (
        <VStack overflow="hidden">
          {/* Hidden content for measurement - always rendered but invisible */}
          <VStack
            position="absolute"
            opacity={0}
            pointerEvents="none"
            width="$full"
            ref={contentRef}
            onLayout={handleCollapseContentLayout}
          >
            {children}
          </VStack>
          {/* Animated visible content */}
          <MotiView
            state={animationState}
            transition={{ type: "timing", duration: 200 }}
          >
            <VStack height={collapseContentFixedHeight}>{children}</VStack>
          </MotiView>
        </VStack>
      )}
    </VStack>
  );
};
